import React, { useMemo, useState } from "react";
import { styled } from "styled-components";
import { AttrValue, ChangeSet, ConfigSchema } from "./configTypes";
import {
  changeSetActor,
  changeSetKey,
  describeAge,
  daysSince,
  EMPTY_DISPLAY,
  fieldByName,
  formatAttr,
  formatDateValue,
  formatDuration,
  orderedHistory,
} from "./configModel";
import { Vehicle } from "./data";
import { Eyebrow } from "./styled";
import { tokens } from "./tokens";
import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../components/ui/sheet";

/**
 * Who changed what, and when — as a tree you open only as far as you need,
 * inside an overlay you open only when you need it.
 *
 * This component is the tree itself; `ChangeHistoryTrigger` below is what the
 * page mounts — one line of "last edited by … " that opens the tree in a sheet.
 * The split is the point: history is the deepest content on the page and the
 * least often read, so it earns a click rather than a column, and the width it
 * frees goes to the record and to whatever metrics land beside it later.
 *
 * Three depths, each a strict elaboration of the line above it:
 *
 *   edit          who, when, and how much moved
 *     field       old value → new value
 *       detail    the stored attribute name, the values in full, and for a
 *                 list, which entries were added or removed
 *     change-set  the audit facts: identifier, actor sub, exact timestamp
 *
 * Flat, this card printed every delta of every edit at once, which on a busy
 * tractor is a wall of text with the one edit you came to read buried in it.
 * Collapsed, the same history reads as a list of events; the detail is a click
 * away rather than gone. Everything an audit needs is still reachable, which is
 * why the deepest level exists at all.
 *
 * The row keeps the last `CONFIG_HISTORY_LIMIT` change-sets; DynamoDB PITR and
 * CloudWatch hold the complete record beyond that, so this is a recent-activity
 * view rather than the audit log itself. It only arrives on the single-vehicle
 * route — the fleet grid deliberately doesn't carry it.
 *
 * Field labels are resolved through the schema, but a delta naming a field the
 * schema no longer has still renders, under its raw attribute name. History is
 * a record of what happened; a later vocabulary change must not quietly erase
 * an entry from it.
 */
interface ChangeHistoryProps {
  vehicle: Vehicle;
  schema: ConfigSchema | null;
  now?: number;
}

const renderValue = (
  schema: ConfigSchema | null,
  name: string,
  value: AttrValue,
): string => {
  const field = fieldByName(schema, name);
  if (!field) {
    if (value === null || value === undefined || value === "") {
      return EMPTY_DISPLAY;
    }
    return Array.isArray(value) ? value.join(", ") : String(value);
  }
  return formatAttr(field, value);
};

const labelFor = (schema: ConfigSchema | null, name: string): string =>
  fieldByName(schema, name)?.label ?? name;

const asList = (value: AttrValue): string[] =>
  Array.isArray(value) ? value : [];

/** Absolute timestamp, falling back to the raw string if it isn't parseable. */
const absolute = (at: string): string => formatDateValue(at) ?? at;

/**
 * When the change took effect (SW-8879): the as-of time, or the entry time
 * for records written before the key existed.
 */
const effectiveOf = (entry: ChangeSet): string =>
  entry.effective_at ?? entry.at;

/** Seconds the change was dated behind its entry, when more than a minute. */
function backdatedBy(entry: ChangeSet): number | null {
  const gap = (Date.parse(entry.at) - Date.parse(effectiveOf(entry))) / 1000;
  return Number.isFinite(gap) && gap > 60 ? gap : null;
}

/**
 * When an edit happened, both ways at once. The relative age is what a reader
 * scans for; the absolute time is what tells two same-day edits apart, which
 * "today" alone cannot. `relative` is null when the timestamp won't parse, and
 * `absolute` then falls back to the raw string rather than blanking.
 */
export function formatWhen(
  iso: string,
  now: number,
): { relative: string | null; absolute: string } {
  const parsed = Date.parse(iso);
  return {
    relative: describeAge(daysSince(iso, now)),
    // The same rendering the "Saved at" fact and the title use, so one edit
    // is not dated two ways on the same screen.
    absolute: Number.isNaN(parsed) ? iso : (formatDateValue(iso) ?? iso),
  };
}

/** "2 fields · 1 re-confirmed", or "no field changes" for an empty one. */
function summarise(entry: ChangeSet): string {
  const changed = Object.keys(entry.changes ?? {}).length;
  const confirmed = entry.confirmed?.length ?? 0;
  const parts: string[] = [];
  if (changed) parts.push(`${changed} field${changed === 1 ? "" : "s"}`);
  if (confirmed) parts.push(`${confirmed} re-confirmed`);
  return parts.join(" · ") || "no field changes";
}

/**
 * One expandable level. A leaf (no children) renders the same row without a
 * control, so the tree lines up whether or not a node happens to go deeper.
 */
const Node: React.FC<{
  open: boolean;
  onToggle: () => void;
  summary: React.ReactNode;
  meta?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ open, onToggle, summary, meta, children }) => (
  <div>
    <Row type="button" onClick={onToggle} aria-expanded={open}>
      <Caret aria-hidden="true" $open={open}>
        ▸
      </Caret>
      <Summary>{summary}</Summary>
      {meta && <Meta>{meta}</Meta>}
    </Row>
    {open && <Nested role="group">{children}</Nested>}
  </div>
);

const ChangeHistory: React.FC<ChangeHistoryProps> = ({
  vehicle,
  schema,
  now = Date.now(),
}) => {
  const entries = useMemo(
    () => orderedHistory(vehicle.history),
    [vehicle.history],
  );

  // Open nodes, by path. A path is stable across re-renders (change-set key,
  // then attribute name), so an entry stays open while the row refreshes
  // underneath it — a save that appends history must not collapse what the
  // operator was reading.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const paths = useMemo(() => {
    const all: string[] = [];
    entries.forEach((entry, index) => {
      const key = changeSetKey(entry, index);
      all.push(key);
      Object.keys(entry.changes ?? {}).forEach((name) =>
        all.push(`${key}/${name}`),
      );
      all.push(`${key}/#audit`);
    });
    return all;
  }, [entries]);

  const allOpen = paths.length > 0 && paths.every((p) => open.has(p));

  const toggle = (path: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(path)) next.add(path);
      return next;
    });

  return (
    <>
      <Header>
        <Eyebrow>Change history</Eyebrow>
        <HeaderRight>
          <Count>
            {entries.length
              ? `last ${entries.length} edit${entries.length === 1 ? "" : "s"}`
              : "none"}
          </Count>
          {entries.length > 0 && (
            <ExpandAll
              type="button"
              onClick={() => setOpen(allOpen ? new Set() : new Set(paths))}
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </ExpandAll>
          )}
        </HeaderRight>
      </Header>

      {entries.length === 0 ? (
        <Empty>
          No recorded edits. Changes made through this console appear here.
        </Empty>
      ) : (
        <List role="list" aria-label="Edits">
          {entries.map((entry, index) => {
            const key = changeSetKey(entry, index);
            const deltas = Object.entries(entry.changes ?? {});
            // The row is dated by when the change took effect: that is what
            // the timeline shows. The entry time sits in the record details.
            const effective = effectiveOf(entry);
            const when = formatWhen(effective, now);
            const backdated = backdatedBy(entry);
            return (
              <Entry key={key} role="listitem">
                <Node
                  open={open.has(key)}
                  onToggle={() => toggle(key)}
                  summary={
                    <>
                      <Actor>{changeSetActor(entry)}</Actor>
                      <When title={absolute(effective)}>
                        {when.relative ?? when.absolute}
                        {when.relative && (
                          <WhenAbsolute> · {when.absolute}</WhenAbsolute>
                        )}
                      </When>
                      {backdated !== null && (
                        <Backdated>
                          Backdated {formatDuration(backdated)}
                        </Backdated>
                      )}
                      {entry.status !== "applied" && (
                        <Status>{entry.status}</Status>
                      )}
                    </>
                  }
                  meta={summarise(entry)}
                >
                  {entry.note && <NoteQuote>{entry.note}</NoteQuote>}
                  {deltas.map(([name, delta]) => {
                    const path = `${key}/${name}`;
                    const before = asList(delta.from);
                    const after = asList(delta.to);
                    const added = after.filter((x) => !before.includes(x));
                    const removed = before.filter((x) => !after.includes(x));
                    return (
                      <Node
                        key={name}
                        open={open.has(path)}
                        onToggle={() => toggle(path)}
                        summary={
                          <>
                            <DeltaField>{labelFor(schema, name)}</DeltaField>
                            <DeltaFrom>
                              {renderValue(schema, name, delta.from)}
                            </DeltaFrom>
                            <span aria-label="becomes">→</span>
                            <DeltaTo>
                              {renderValue(schema, name, delta.to)}
                            </DeltaTo>
                            {/* A flip the API made because of another field
                                in the same edit (tractor_config.RULES). Said
                                here, on the row, so the reader does not
                                attribute it to the editor's own hand. */}
                            {delta.rule && (
                              <DeltaRule>
                                Applied by rule: {delta.rule}
                              </DeltaRule>
                            )}
                          </>
                        }
                      >
                        <Facts>
                          <dt>Stored as</dt>
                          <Mono>{name}</Mono>
                          <dt>Was</dt>
                          <dd>{renderValue(schema, name, delta.from)}</dd>
                          <dt>Now</dt>
                          <dd>{renderValue(schema, name, delta.to)}</dd>
                          {/* A list's before/after says what it is now; this
                              says what moved, which is the thing a reader is
                              scanning a route change for. */}
                          {added.length > 0 && (
                            <>
                              <dt>Added</dt>
                              <dd>{added.join(", ")}</dd>
                            </>
                          )}
                          {removed.length > 0 && (
                            <>
                              <dt>Removed</dt>
                              <dd>{removed.join(", ")}</dd>
                            </>
                          )}
                        </Facts>
                      </Node>
                    );
                  })}

                  {(entry.confirmed ?? []).map((name) => (
                    <Confirmation key={`confirm-${name}`}>
                      <Tick aria-hidden="true">✓</Tick>
                      <DeltaField>{labelFor(schema, name)}</DeltaField>
                      <ConfirmNote>re-checked, value unchanged</ConfirmNote>
                    </Confirmation>
                  ))}

                  <Node
                    open={open.has(`${key}/#audit`)}
                    onToggle={() => toggle(`${key}/#audit`)}
                    summary={<AuditLabel>Record details</AuditLabel>}
                  >
                    <Facts>
                      {backdated !== null ? (
                        <>
                          <dt>As of</dt>
                          <dd>
                            {absolute(effective)}
                            {when.relative && ` · ${when.relative}`}
                          </dd>
                          <dt>Entered</dt>
                          <dd>{absolute(entry.at)}</dd>
                        </>
                      ) : (
                        <>
                          <dt>Saved at</dt>
                          <dd>
                            {absolute(entry.at)}
                            {when.relative && ` · ${when.relative}`}
                          </dd>
                        </>
                      )}
                      <dt>Status</dt>
                      <dd>{entry.status}</dd>
                      {entry.actor_email && (
                        <>
                          <dt>Edited by</dt>
                          <dd>{entry.actor_email}</dd>
                        </>
                      )}
                      {/* The sub, not the address, is what an audit joins on:
                          addresses get renamed and reassigned. */}
                      {entry.actor_sub && (
                        <>
                          <dt>Editor ID</dt>
                          <Mono>{entry.actor_sub}</Mono>
                        </>
                      )}
                      {entry.id && (
                        <>
                          <dt>Record ID</dt>
                          <Mono>{entry.id}</Mono>
                        </>
                      )}
                    </Facts>
                  </Node>
                </Node>
              </Entry>
            );
          })}
        </List>
      )}
    </>
  );
};

/**
 * The one line the page keeps, and the way into the rest.
 *
 * History is reference material — you consult it when the record looks wrong,
 * not on every visit — so the tree lives in an overlay and the page keeps only
 * what is worth a glance: who last touched this tractor, when, and how much
 * moved. That single line is the part people actually read, and it is the part
 * that makes the overlay worth opening when it isn't.
 */
export const ChangeHistoryTrigger: React.FC<{
  vehicle: Vehicle;
  schema: ConfigSchema | null;
  now?: number;
}> = ({ vehicle, schema, now = Date.now() }) => {
  const entries = orderedHistory(vehicle.history);
  const latest = entries[0];
  const when = latest ? formatWhen(latest.at, now) : null;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <TriggerRow type="button">
          <Eyebrow>Change history</Eyebrow>
          <TriggerSummary>
            {latest ? (
              <>
                <Actor>{changeSetActor(latest)}</Actor>
                <When title={absolute(latest.at)}>
                  {when?.relative ?? when?.absolute}
                  {when?.relative && (
                    <WhenAbsolute> · {when.absolute}</WhenAbsolute>
                  )}
                </When>
                <Count>{summarise(latest)}</Count>
              </>
            ) : (
              <Count>No recorded edits</Count>
            )}
          </TriggerSummary>
          <TriggerCta aria-hidden="true">
            {entries.length > 1
              ? `All ${entries.length} edits →`
              : "Open history →"}
          </TriggerCta>
        </TriggerRow>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <div>
            <SheetTitle>Change history</SheetTitle>
            <SheetDescription>
              {vehicle.displayName ?? vehicle.vehicleName} · the last{" "}
              {entries.length} recorded edit
              {entries.length === 1 ? "" : "s"}. Only the most recent edits are
              kept here — ask engineering if you need older history.
            </SheetDescription>
          </div>
          <SheetClose asChild>
            <Button variant="ghost" size="sm">
              Close
            </Button>
          </SheetClose>
        </SheetHeader>
        <SheetBody>
          <ChangeHistory vehicle={vehicle} schema={schema} now={now} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
};

const TriggerRow = styled.button`
  display: flex;
  align-items: baseline;
  gap: 12px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  background: none;
  border: none;
  border-top: 1px solid ${tokens.line};
  padding: 12px 20px;
  cursor: pointer;

  &:hover {
    background: ${tokens.offWhite};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: -2px;
  }
`;

const TriggerSummary = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const TriggerCta = styled.span`
  flex: none;
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.blue};
`;

// Pinned to the top of the sheet body while the tree scrolls under it, so
// "Collapse all" is still in reach from the bottom of a fully opened history.
// Positioned above the rows: a caret is transformed, which would otherwise
// paint it through the header as the row passes underneath.
const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 20px 8px;
  background: ${tokens.white};
  border-bottom: 1px solid ${tokens.line};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const Count = styled.span`
  font-size: 12.5px;
  color: ${tokens.slate};
`;

const ExpandAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.blue};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 2px;
  }
`;

// No height of its own: the sheet body is the scroll region, and the tree
// takes whatever height it is given. The cap this used to carry -- 70vh, from
// when the tree was a card in the page column -- came along into the sheet, so
// the list stopped two-thirds of the way down, scrolled inside itself, and
// left the rest of the sheet blank.
const List = styled.div`
  /* The pinned header above draws this line. */
  & > :first-child {
    border-top: 0;
  }
`;

const Entry = styled.div`
  padding: 4px 14px 4px 8px;
  border-top: 1px solid ${tokens.line};
`;

const Row = styled.button`
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  border-radius: 6px;
  padding: 7px 8px;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: ${tokens.offWhite};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: -2px;
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  flex: none;
  font-size: 10px;
  line-height: 1.6;
  color: ${tokens.slate};
  transform: rotate(${(p) => (p.$open ? "90deg" : "0deg")});
  transition: transform 0.12s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Summary = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 7px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: ${tokens.slate};
`;

const Meta = styled.span`
  flex: none;
  font-size: 11.5px;
  color: ${tokens.slate};
  white-space: nowrap;
`;

// The nesting rule: one vertical line per level, which is what makes the depth
// of a row readable at a glance rather than inferred from its indent.
const Nested = styled.div`
  margin: 2px 0 6px 13px;
  padding-left: 8px;
  border-left: 1px solid ${tokens.line};
`;

const Actor = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${tokens.ink};
  /* An email has no break opportunity; in a phone-width trigger row it ran
     under the "All n edits" call to action. Let it break rather than overlap. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

const When = styled.span`
  font-size: 12px;
  color: ${tokens.slate};
`;

// Quieter than the relative age it follows: the age is what the eye scans for,
// the clock time is what settles the order of two edits made the same day.
const WhenAbsolute = styled.span`
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
`;

/** The change was dated behind its entry: the timeline was rewritten. */
const Backdated = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${tokens.majorText};
  background: rgba(255, 150, 0, 0.12);
  border: 1px solid rgba(255, 150, 0, 0.35);
  border-radius: 4px;
  padding: 1px 6px;
`;

/** The note that travelled with the change onto the timeline. */
const NoteQuote = styled.blockquote`
  margin: 4px 0 8px 21px;
  padding: 6px 10px;
  border-left: 3px solid ${tokens.toggleOff};
  border-radius: 4px;
  background: ${tokens.offWhite};
  font-size: 12.5px;
  color: ${tokens.ink};
`;

const Status = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${tokens.majorText};
  background: rgba(255, 150, 0, 0.12);
  border: 1px solid rgba(255, 150, 0, 0.35);
  border-radius: 4px;
  padding: 1px 6px;
`;

const DeltaField = styled.span`
  font-weight: 600;
  color: ${tokens.navy};
`;

const DeltaFrom = styled.span`
  text-decoration: line-through;
`;

const DeltaTo = styled.span`
  color: ${tokens.ink};
  font-weight: 500;
`;

const DeltaRule = styled.span`
  flex-basis: 100%;
  font-size: 11.5px;
  color: ${tokens.slate};
`;

const Confirmation = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
  flex-wrap: wrap;
  padding: 7px 8px 7px 25px;
  font-size: 12.5px;
  color: ${tokens.slate};
`;

const Tick = styled.span`
  flex: none;
  font-size: 11px;
  color: ${tokens.readyText};
`;

const ConfirmNote = styled.span`
  font-size: 12px;
`;

const AuditLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${tokens.slate};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 3px 12px;
  margin: 4px 0 8px;
  padding-left: 21px;
  font-size: 12px;

  dt {
    color: ${tokens.slate};
    white-space: nowrap;
  }
  dd {
    margin: 0;
    min-width: 0;
    color: ${tokens.ink};
    overflow-wrap: anywhere;
  }
`;

const Mono = styled.dd`
  margin: 0;
  min-width: 0;
  font-family: ${tokens.fontMono};
  font-size: 11.5px;
  color: ${tokens.ink};
  overflow-wrap: anywhere;
`;

const Empty = styled.div`
  padding: 4px 20px 18px;
  font-size: 13px;
  color: ${tokens.slate};
`;

export default ChangeHistory;
